import React, { useEffect, useState } from 'react';
import { Circle, Close } from '@mui/icons-material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import {
  Inbox,
  SuprSendFeedProvider,
  SuprSendProvider,
  useFeedClient,
  useSuprSendClient
} from '@suprsend/react';
import dayjs from 'dayjs';

import { fetchNotifications } from '@/adapters/fetchExaCare';
import NotificationToast from '@/components/ui/Toast/NotificationToast';
import { useSuprSendJwtToken } from '@/hooks/useSuprSendJwtToken';
import { Placeholder } from '@/pages/CRM/components/Placeholder';

interface SuprSendTokenPayload {
  token: string;
  expiresAt: number;
}

const WORKSPACE_KEY = import.meta.env
  .VITE_SUPR_SEND_CLIENT_SIDE_API_KEY as string;
const TOAST_AUTO_HIDE_DELAY = 6000;

const INBOX_THEME = {
  badge: {
    backgroundColor: '#1DB8F2',
    color: 'white',
    fontSize: '12px',
    fontWeight: 'bold',
    top: '-5px',
    right: '-5px'
  },
  notification: {
    container: {
      backgroundColor: '#FFFFFF'
    }
  },
  header: {
    markAllReadText: { color: '#1DB8F2', fontWeight: 600 }
  }
} as const;

interface InboxModalProps {
  userId: string;
}

interface ToastNotificationProps {
  onNewNotification: (data: any) => void;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({
  onNewNotification
}) => {
  const feedClient = useFeedClient();

  useEffect(() => {
    if (!feedClient) {
      return;
    }

    if (!feedClient.emitter) {
      return;
    }

    feedClient.emitter.on('feed.new_notification', (data) => {
      onNewNotification(data);
    });

    return () => {
      if (feedClient.emitter) {
        feedClient.emitter.off('feed.new_notification');
      }
    };
  }, [feedClient, onNewNotification]);

  return null;
};

interface InboxWithNotificationsProps {
  userId: string;
  userToken?: string;
}

const InboxWithNotifications: React.FC<InboxWithNotificationsProps> = ({
  userId,
  userToken
}) => {
  const suprSendClient = useSuprSendClient();
  const [showToast, setShowToast] = useState(false);
  const [toastData, setToastData] = useState<any>(null);

  useEffect(() => {
    if (suprSendClient && userToken) {
      suprSendClient.identify(userId, userToken);
    }
  }, [suprSendClient, userId, userToken]);

  const handleNewNotification = (data: any) => {
    setToastData(data);
    setShowToast(true);

    setTimeout(() => {
      setShowToast(false);
      setToastData(null);
    }, TOAST_AUTO_HIDE_DELAY);
  };

  return (
    <SuprSendFeedProvider>
      <Inbox
        hideAvatar
        disableAutomaticSeenTracking
        bellComponent={() => <NotificationsIcon htmlColor="#364955" />}
        notificationComponent={(data) => {
          const { notificationData, markAsArchived } = data;

          return (
            <Stack
              direction="column"
              sx={{
                borderTop: '1px solid #D3E5F4',
                cursor: !notificationData.read_on ? 'pointer' : 'default',
                padding: '16px'
              }}
              bgcolor={!notificationData.read_on ? '#EEF4F9' : '#FFFFFF'}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                pb="4px"
                alignItems="flex-start"
              >
                <Stack flex={1.4}>
                  <Typography
                    color="#0A1E28"
                    fontWeight={500}
                    fontSize="16px"
                    display="flex"
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <Circle
                      htmlColor={
                        notificationData.seen_on ? '#B5C9D6' : '#1DB8F2'
                      }
                      sx={{
                        width: '6px',
                        height: '6px',
                        marginRight: '8px',
                        marginTop: '8px'
                      }}
                    />
                    {notificationData.message.header}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center">
                  <Typography color="#667A86" fontWeight={400} fontSize="10px">
                    {dayjs(notificationData.created_on).format('MMM D h:mm A')}
                  </Typography>
                  <IconButton onClick={() => markAsArchived()} size="small">
                    <Close htmlColor="#667A86" sx={{ width: 16, height: 16 }} />
                  </IconButton>
                </Stack>
              </Stack>
              <Typography color="#0A1E28" fontSize="14px" fontWeight={400}>
                {notificationData.message.text}
              </Typography>
              {notificationData?.message?.subtext && (
                <Typography
                  color="#364955"
                  fontSize="12px"
                  fontWeight={400}
                  mt={1}
                >
                  {notificationData.message.subtext.text}
                </Typography>
              )}
              {notificationData?.message?.actions &&
                notificationData.message.actions.length > 0 && (
                  <Stack direction="row" gap={2} mt={1}>
                    {notificationData.message.actions.map(
                      (action: any, index: number) => (
                        <Button
                          size="medium"
                          variant="contained"
                          sx={{
                            marginTop: '8px',
                            cursor: 'pointer'
                          }}
                          color={index === 0 ? 'primary' : 'secondary'}
                          onClick={() => {
                            if (action.open_in_new_tab) {
                              window.open(action.url, '_blank');
                            } else {
                              window.location.href = action.url;
                            }
                          }}
                          key={index}
                        >
                          {action.name}
                        </Button>
                      )
                    )}
                  </Stack>
                )}
            </Stack>
          );
        }}
        noNotificationsComponent={() => (
          <Placeholder
            Icon={NotificationsIcon}
            sx={{ textAlign: 'center' }}
            header="There are no unread notifications"
          />
        )}
        theme={INBOX_THEME}
      />
      <ToastNotification onNewNotification={handleNewNotification} />

      {showToast && toastData && (
        <Box
          sx={{
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 9999,
            minWidth: '380px'
          }}
        >
          <NotificationToast
            title={toastData.message.header || ''}
            message={
              toastData.message.text +
              (toastData?.message?.subtext
                ? `\n${toastData.message.subtext.text}`
                : '')
            }
            actions={
              toastData?.message?.actions &&
              toastData.message.actions.length > 0 ? (
                <Stack
                  direction="row"
                  gap={1}
                  mt={1}
                  sx={{ paddingTop: '8px' }}
                >
                  {toastData.message.actions.map(
                    (action: any, index: number) => (
                      <Button
                        key={index}
                        size="small"
                        variant="contained"
                        color={index === 0 ? 'primary' : 'secondary'}
                        onClick={() => {
                          if (action.open_in_new_tab) {
                            window.open(action.url, '_blank');
                          } else {
                            window.location.href = action.url;
                          }
                        }}
                        sx={{
                          fontSize: '12px',
                          padding: '4px 12px',
                          minWidth: 'auto',
                          cursor: 'pointer'
                        }}
                      >
                        {action.name}
                      </Button>
                    )
                  )}
                </Stack>
              ) : null
            }
            dismissToast={() => setShowToast(false)}
          />
        </Box>
      )}
    </SuprSendFeedProvider>
  );
};

const InboxModal: React.FC<InboxModalProps> = ({ userId }) => {
  const { data: tokenData, isLoading, error } = useSuprSendJwtToken(userId);

  if (isLoading) {
    return (
      <IconButton>
        <NotificationsIcon htmlColor="#364955" />
      </IconButton>
    );
  }

  if (error) {
    return (
      <Stack justifyContent="center" alignItems="center" sx={{ p: 2 }}>
        <Typography color="error">Failed to load notifications</Typography>
      </Stack>
    );
  }

  const handleRefreshToken = async () => {
    const response = await fetchNotifications.post<SuprSendTokenPayload>(
      `/suprsend/generate-jwt-token`,
      {
        user_id: userId,
        tenant_id: 'default',
        expires_in: 3600
      }
    );
    return response.token;
  };

  return (
    <SuprSendProvider
      publicApiKey={WORKSPACE_KEY}
      distinctId={userId}
      userToken={tokenData?.token}
      refreshUserToken={handleRefreshToken}
    >
      <InboxWithNotifications userId={userId} userToken={tokenData?.token} />
    </SuprSendProvider>
  );
};

export default InboxModal;
